import { flushSync } from 'react-dom';
import { createDeparture, showNavigationError } from './portalDeparture';
import { prefersReducedMotion, REDUCED_MS } from './portalMotion';
import './portalTransitions.css';
import { animatePortalSurface } from './portalSurface';

export const FAST_MS = 150;
export const HOLD_MAX_MS = 1200;
let active = null;
let nextTest = null;
// The dev-only motion-check page exercises the real route path with deterministic network timing.
export function testNextPortalLoad(ms, fail = false) {
  if (process.env.NODE_ENV === 'development') nextTest = { ms, fail };
}
export const isPortalMoving = () => !!active;
/** What the running motion is: 'entry', 'viewer-open', 'viewer-close', or 'route' for a page change. */
export const portalMotionMode = () => (active ? active.mode || 'route' : null);
export function cancelPortalNavigation(region) {
  if (region && active?.surface?.region !== region) return;
  active?.cancel(); active = null;
  delete document.documentElement.dataset.prDir;
  delete document.documentElement.dataset.portalTransition;
  ['x', 'y', 'ring-left', 'ring-top', 'core-left', 'core-top', 'hole', 'cover', 'ring-cover'].forEach(key => document.documentElement.style.removeProperty(`--pr-${key}`));
}

/** One DOM commit, one destination tree. Browser snapshots own the THROUGH animation. */
export async function portalNavigate({ commit, origin, load, back = false, mode, ringSize, onFailure, surface, doorway = false }) {
  if (active) return false;
  origin = origin || { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  const viewer = mode?.startsWith('viewer');
  const departure = createDeparture(origin, { viewer, ringSize, surface, doorway });
  let transition, cancelled = false, committed = false;
  const timers = new Set();
  let abort;
  const aborted = new Promise(resolve => { abort = () => resolve('cancel'); });
  const delay = (ms, value) => new Promise(resolve => { const id = setTimeout(() => { timers.delete(id); resolve(value); }, ms); timers.add(id); });
  const job = { surface, mode, cancel() { cancelled = true; abort(); timers.forEach(clearTimeout); transition?.skipTransition(); departure.stop(); } };
  active = job;
  const test = nextTest; nextTest = null;
  const root = document.documentElement;
  try {
    // The cap starts at the click, not 150ms later. Observe rejection immediately.
    const loaded = Promise.resolve().then(() => load?.());
    const data = (test ? Promise.all([loaded, delay(test.ms)]).then(([value]) => { if (test.fail) throw new Error('Test load failed'); return value; }) : loaded)
      .then(value => ({ status: 'ok', value }), () => ({ status: 'fail' }));
    const cap = delay(HOLD_MAX_MS, { status: 'late' });
    let result = { status: 'ok' };
    if (!back && mode !== 'viewer-close') {
      result = await Promise.race([data, delay(FAST_MS, { status: 'hold' }), aborted]);
      if (result === 'cancel') return false;
      if (result.status === 'hold') {
        departure.start();
        result = await Promise.race([data, cap, aborted]);
      }
      if (result === 'cancel') return false;
      if (result.status === 'fail') {
        await departure.fail();
        if (!cancelled) { showNavigationError(); onFailure?.(); }
        return false;
      }
    }
    if (cancelled) return false;
    root.style.setProperty('--pr-x', `${origin.x}px`);
    root.style.setProperty('--pr-y', `${origin.y}px`);
    root.dataset.prDir = mode || (back ? 'back' : 'fwd');
    root.dataset.portalTransition = '1';
    departure.snapshot(back);
    const change = () => {
      if (cancelled || committed) return;
      committed = true;
      departure.commit();
      flushSync(() => commit(result.value));
    };
    const reduced = prefersReducedMotion();
    if (surface) {
      transition = animatePortalSurface({ surface, origin, departure, change, back, reduced });
      await transition.finished;
    } else if (typeof document.startViewTransition !== 'function' || reduced || document.hidden) {
      departure.stop(); change();
      const page = document.querySelector(viewer ? '[data-pr-viewer]' : '[data-pr-content]');
      const animation = page?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: REDUCED_MS });
      await Promise.race([delay(REDUCED_MS), aborted]);
      animation?.cancel();
    } else {
      transition = document.startViewTransition(change);
      // A duplicate snapshot name or browser interruption must never swallow navigation.
      await transition.finished.catch(() => {});
      change();
    }
    return !cancelled;
  } finally {
    timers.forEach(clearTimeout); departure.stop();
    if (active === job) {
      active = null;
      delete root.dataset.prDir; delete root.dataset.portalTransition;
      ['x', 'y', 'ring-left', 'ring-top', 'core-left', 'core-top', 'hole', 'cover', 'ring-cover'].forEach(key => root.style.removeProperty(`--pr-${key}`));
    }
  }
}
